import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Check, Phone, Shield, FileText, Zap, ArrowRight, Database, Users, MessageSquare, Brain, LayoutGrid, Lock, CheckCircle, Smartphone, Globe, FileCheck, Clock, Activity, AlertCircle, ScanLine, XCircle, Mic, Sparkles } from 'lucide-react';

// Import Assets
import amandaImg from '../assets/amanda.png';
import amyAvatar from '../assets/amy-avatar.png';

const InsuranceVerificationPage = () => {
  const [activeFeature, setActiveFeature] = useState('verification');

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 0.5; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        .animate-pulse-ring {
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.07);
        }
        .scan-line {
          width: 100%;
          height: 2px;
          background: #3b82f6;
          box-shadow: 0 0 4px #3b82f6;
          animation: scan 2s linear infinite;
        }
        @keyframes scan {
          0% { transform: translateY(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(100px); opacity: 0; }
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Meet Amanda, Your AI Insurance Expert</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold leading-tight tracking-tight">
                <span className="text-gray-900">Amanda, The Only</span><br />
                <span className="text-blue-600">Dual-Source</span> Insurance Verification AI
              </h1>
              
              <p className="text-base text-gray-700 leading-relaxed max-w-lg">
                Amanda uses both <span className="font-semibold text-blue-600">300+ direct payer integrations</span> AND <span className="font-semibold text-blue-600">AI phone calls</span> to verify benefits, eligibility, and procedure history—saving your staff 30-45 minutes per patient.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <button className="px-6 py-2.5 rounded-lg font-medium text-sm transition-all bg-white text-gray-900 hover:bg-gray-50 border border-gray-300 shadow-sm">
                  Get a demo
                </button>
                <button className="px-6 py-2.5 rounded-lg font-medium text-sm transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md">
                  See pricing
                </button>
              </div>
            </div>
            
            {/* Right: Interactive AI Visual */}
            <div className="relative flex items-center justify-center h-[500px]">
              {/* Main Profile with Card */}
              <div className="relative">
                {/* Main Image Card with Gradient Glow */}
                <div className="relative p-1 rounded-3xl bg-gradient-to-br from-blue-300 via-blue-400 to-indigo-400 shadow-xl">
                  <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-[22px] overflow-hidden w-72 h-96">
                    <img
                      src="/assets/amanda.png"
                      alt="Amanda AI Insurance Agent"
                      className="w-full h-full object-cover opacity-95"
                    />
                  
                  {/* Glowing circle indicator on ear */}
                  <div className="absolute top-32 left-12">
                    <div className="relative">
                      <div className="w-8 h-8 rounded-full bg-cyan-300 animate-pulse"></div>
                      <div className="absolute inset-0 w-8 h-8 rounded-full bg-cyan-200 animate-ping"></div>
                    </div>
                  </div>
                  
                  {/* Status at bottom */}
                  <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-2xl px-6 py-3 shadow-md border border-gray-200 w-max">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                         <Globe className="w-4 h-4 text-blue-500 animate-pulse" />
                         <span className="text-xs font-bold text-gray-700">API</span>
                      </div>
                      <div className="w-px h-4 bg-gray-300"></div>
                      <div className="flex items-center gap-1">
                         <Phone className="w-4 h-4 text-green-500 animate-pulse" />
                         <span className="text-xs font-bold text-gray-700">Call</span>
                      </div>
                      <div className="w-px h-4 bg-gray-300"></div>
                      <div className="text-xs font-medium text-blue-600">Verifying...</div>
                    </div>
                  </div>
                </div>
                </div>
                
                {/* Speech bubble - top right */}
                <div className="absolute -top-4 -right-20 bg-white rounded-2xl p-4 shadow-md w-60 border border-gray-200">
                  <p className="text-xs text-gray-600 leading-relaxed">
                    <span className="font-bold text-blue-600">Verified:</span> Delta Dental. 100% Preventive, 80% Basic. $1,500 Max remaining.
                  </p>
                  {/* Tail */}
                  <div className="absolute bottom-6 left-0 w-3 h-3 bg-white transform rotate-45 -translate-x-1.5 border-l border-b border-gray-200"></div>
                </div>
                
                {/* Connection Badge - bottom left */}
                <div className="absolute bottom-20 -left-16 bg-white rounded-xl px-3 py-2 shadow-sm border border-gray-200">
                  <div className="flex items-center gap-2">
                    <Database className="w-3.5 h-3.5 text-indigo-500" />
                    <span className="text-xs font-medium text-gray-600">Saved in Asha PMS</span>
                  </div>
                </div>
                
                {/* Stats card - top left */}
                <div className="absolute top-10 -left-12 bg-white rounded-xl p-3 shadow-sm border border-gray-200">
                  <div className="flex items-center gap-2">
                    <Shield className="w-3.5 h-3.5 text-green-500" />
                    <span className="text-xs font-medium text-gray-700">Eligible</span>
                  </div>
                </div>
                
                {/* Efficiency Stats - bottom right */}
                <div className="absolute -bottom-6 -right-16 bg-white rounded-2xl p-4 shadow-md border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="relative w-12 h-12 flex items-center justify-center bg-blue-50 rounded-full text-blue-600 font-bold text-sm">
                      45m
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700">Saved Per Patient</p>
                      <p className="text-xs text-gray-400">vs Manual Calls</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section - Text Left, Audio Right */}
      <section className="py-20 px-6 bg-white overflow-hidden border-b border-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div className="space-y-8 relative z-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-xs font-medium border border-blue-100 mb-4 uppercase tracking-wide">
                  Hear The Difference
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  She doesn't just read portals.<br/>
                  <span className="text-blue-600">She picks up the phone.</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Most "AI" verification tools only check online portals, missing critical details like history, frequencies, and downgrades.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed mt-4">
                  Amanda is the only agent that will <strong>call insurance companies</strong>, wait on hold, and speak to representatives to get the "hard-to-find" information, ensuring 100% accurate breakdowns.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <button className="px-8 py-4 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5">
                  Book a Demo
                </button>
                <button className="px-8 py-4 rounded-xl font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all">
                  View Pricing
                </button>
              </div>

            </div>

            {/* Right: Audio Demo Player */}
            <div className="relative">
              {/* Background Decorative Elements */}
              <div className="absolute -top-10 -right-10 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
              <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
              
              <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
                {/* Player Header */}
                <div className="bg-[#0f172a] p-8 text-white">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-2">Live Verification Demos</h3>
                      <p className="text-gray-400 text-sm">Listen to Amanda speak with payers</p>
                    </div>
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <Phone className="w-5 h-5 text-blue-400" />
                    </div>
                  </div>
                </div>

                {/* Playlist */}
                <div className="p-6 space-y-4">
                  {/* Track 1 */}
                  <div className="group bg-gray-50 hover:bg-blue-50 rounded-xl p-4 transition-all border border-gray-100 hover:border-blue-100 cursor-pointer">
                    <div className="flex items-center gap-4">
                      <button className="w-12 h-12 rounded-full bg-white shadow-sm group-hover:bg-blue-600 group-hover:text-white flex items-center justify-center transition-all text-blue-600">
                        <div className="w-0 h-0 border-l-[6px] border-l-current border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5"></div>
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">Verifying Code Frequency</h4>
                          <span className="text-xs font-mono text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200">2:45</span>
                        </div>
                        <p className="text-sm text-gray-500 group-hover:text-blue-600/80 transition-colors">Asking rep about D2740 frequency limitations</p>
                      </div>
                    </div>
                  </div>

                  {/* Track 2 */}
                  <div className="group bg-gray-50 hover:bg-blue-50 rounded-xl p-4 transition-all border border-gray-100 hover:border-blue-100 cursor-pointer">
                    <div className="flex items-center gap-4">
                      <button className="w-12 h-12 rounded-full bg-white shadow-sm group-hover:bg-blue-600 group-hover:text-white flex items-center justify-center transition-all text-blue-600">
                        <div className="w-0 h-0 border-l-[6px] border-l-current border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5"></div>
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">Missing Subscriber Info</h4>
                          <span className="text-xs font-mono text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200">1:30</span>
                        </div>
                        <p className="text-sm text-gray-500 group-hover:text-blue-600/80 transition-colors">Locating policy with partial patient data</p>
                      </div>
                    </div>
                  </div>

                  {/* Track 3 */}
                  <div className="group bg-gray-50 hover:bg-blue-50 rounded-xl p-4 transition-all border border-gray-100 hover:border-blue-100 cursor-pointer">
                    <div className="flex items-center gap-4">
                      <button className="w-12 h-12 rounded-full bg-white shadow-sm group-hover:bg-blue-600 group-hover:text-white flex items-center justify-center transition-all text-blue-600">
                        <div className="w-0 h-0 border-l-[6px] border-l-current border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5"></div>
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">Benefits Summary to Staff</h4>
                          <span className="text-xs font-mono text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200">0:45</span>
                        </div>
                        <p className="text-sm text-gray-500 group-hover:text-blue-600/80 transition-colors">Explaining coverage highlights to your team</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Player Footer */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <Shield className="w-3.5 h-3.5" />
                    <span>HIPAA Compliant & Secure</span>
                  </div>
                  <span>Waits on hold for you</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW SECTION: The Only AI That Truly Understands Dentistry (VoiceStack Style) */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-16 max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    Complete <span className="text-blue-600">Insurance Automation</span><br/>
                    from Payer to Asha PMS
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                    Amanda is the only solution that combines 300+ direct payer integrations with AI-driven phone calls to deliver the most accurate benefits breakdowns in the industry.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Card 1: 300+ Payer Integrations */}
                <div className="flex flex-col">
                    {/* Visual Area - Glassmorphic on Gradient */}
                    <div className="bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center shadow-sm">
                         {/* Background Orbs */}
                         <div className="absolute top-0 right-0 w-64 h-64 bg-blue-300/30 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>
                         <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-300/30 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3"></div>
                         
                         {/* Glass Card Container */}
                         <div className="relative w-full max-w-md">
                            <div className="glass-card rounded-3xl p-6 relative z-20 bg-white/60 backdrop-blur-xl border border-white/60 shadow-xl">
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                            <Globe className="w-6 h-6 text-blue-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 text-lg">Payer Network</h3>
                                            <p className="text-sm text-blue-600 font-medium">300+ Integrations</p>
                                        </div>
                                    </div>
                                    <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold">
                                        Active
                                    </div>
                                </div>

                                {/* Integrations Visualization */}
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3 animate-float" style={{ animationDelay: '0s' }}>
                                        <div className="flex-1 bg-white p-4 rounded-xl shadow-sm border border-blue-50">
                                            <div className="flex justify-between items-center mb-2">
                                                <div className="flex items-center gap-2">
                                                    <Shield className="w-4 h-4 text-blue-600" />
                                                    <span className="text-xs font-bold text-gray-800">Delta Dental</span>
                                                </div>
                                                <span className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-bold">Verified</span>
                                            </div>
                                            <div className="space-y-1.5">
                                                <div className="flex justify-between text-[10px] text-gray-500">
                                                    <span>Deductible</span>
                                                    <span className="font-bold text-gray-700">$50.00 / $50.00</span>
                                                </div>
                                                <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                                                    <div className="w-full bg-green-500 h-full rounded-full"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-3 animate-float" style={{ animationDelay: '1s' }}>
                                        <div className="flex-1 bg-white p-4 rounded-xl shadow-sm border border-blue-50 opacity-90 transform scale-95">
                                            <div className="flex justify-between items-center mb-2">
                                                 <div className="flex items-center gap-2">
                                                    <Shield className="w-4 h-4 text-indigo-600" />
                                                    <span className="text-xs font-bold text-gray-800">MetLife</span>
                                                </div>
                                                <span className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-bold">Verified</span>
                                            </div>
                                             <div className="space-y-1.5">
                                                <div className="flex justify-between text-[10px] text-gray-500">
                                                    <span>Max Remaining</span>
                                                    <span className="font-bold text-gray-700">$1,200 / $1,500</span>
                                                </div>
                                                <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                                                    <div className="w-4/5 bg-blue-500 h-full rounded-full"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                         </div>
                    </div>

                    {/* Text Content */}
                    <div>
                        <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <Zap className="w-4 h-4" />
                             Instant Verification
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">300+ Dental Payer Integrations</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Amanda connects directly to over 300 dental payers to instantly pull eligibility, deductibles, and comprehensive benefits breakdowns without any manual work.
                        </p>
                    </div>
                </div>

                {/* Card 2: AI Insurance Calls */}
                <div className="flex flex-col">
                    {/* Visual Area */}
                    <div className="bg-gradient-to-br from-indigo-50 via-white to-blue-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center shadow-sm border border-indigo-100">
                         {/* Background Grid */}
                         <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:20px_20px]"></div>
                         
                        <div className="relative w-full max-w-md z-10 flex flex-col items-center justify-center">
                             <div className="glass-card rounded-3xl p-6 w-full bg-white/80 backdrop-blur-xl border border-white/60 shadow-xl">
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center shadow-sm">
                                            <Phone className="w-6 h-6 text-green-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 text-lg">AI Phone Call</h3>
                                            <p className="text-sm text-green-600 font-medium">Speaking to Rep...</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 bg-green-100 px-3 py-1 rounded-full">
                                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                        <span className="text-xs font-bold text-green-700">04:12</span>
                                    </div>
                                </div>

                                {/* Call Visualization */}
                                <div className="space-y-4">
                                    {/* Waveform */}
                                    <div className="flex items-center justify-center gap-1 h-8 bg-gray-50 rounded-xl mb-4">
                                        {[1, 3, 2, 5, 3, 7, 4, 6, 3, 5, 2, 4, 2, 3, 1].map((h, i) => (
                                            <div
                                                key={i}
                                                className="w-1 bg-green-500 rounded-full animate-pulse"
                                                style={{ height: `${h * 10}%`, animationDelay: `${i * 0.05}s` }}
                                            ></div>
                                        ))}
                                    </div>

                                    {/* Action Item */}
                                    <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm flex items-start gap-3">
                                        <div className="bg-blue-50 p-2 rounded-lg">
                                            <Clock className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-gray-900">Retrieving History</p>
                                            <p className="text-[10px] text-gray-500">"Can you confirm the last service date for D2740?"</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Text Content */}
                    <div>
                        <div className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <Phone className="w-4 h-4" />
                             Hard-to-Get Info
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">AI Insurance Phone Calls</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            For missing info or procedure histories, Amanda calls the payer, waits on hold, and speaks to a representative to get comprehensive details that portals don't show.
                        </p>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* REFINED SECTION: Bento Grid Features */}
      <section className="py-24 px-6 bg-white border-t border-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-16 max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Powerful Features for <br />
              <span className="text-blue-600">Modern Dental Teams</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to automate insurance workflows, reduce write-offs, and empower your staff.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Card 1 - Detailed Benefits Breakdowns (Span 2) */}
            <div className="md:col-span-2 bg-gray-50 rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10 max-w-lg">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm text-blue-600">
                  <FileCheck className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Detailed Procedure Breakdowns</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Get granular coverage details for specific procedure codes (D2740, D4341, etc.). We calculate patient portions, deductibles, and remaining maximums accurately.
                </p>
              </div>
              
              {/* Graphic Overlay */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[45%] h-full hidden md:flex items-center justify-center p-6">
                 <div className="relative w-full max-w-xs space-y-3 transform scale-95 hover:scale-100 transition-transform duration-500">
                     {/* Breakdown Item 1 */}
                     <div className="glass-card bg-white/95 p-4 rounded-xl shadow-lg border-l-4 border-l-green-500 flex flex-col gap-2">
                         <div className="flex justify-between items-center">
                             <div className="flex items-center gap-2">
                                <div className="bg-green-100 p-1 rounded text-green-700 font-mono text-[10px] font-bold">D2740</div>
                                <span className="text-xs font-bold text-gray-800">Crown - Porcelain/Ceramic</span>
                             </div>
                             <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">50% Covered</span>
                         </div>
                         <div className="grid grid-cols-2 gap-2 text-[10px] text-gray-500 border-t border-gray-100 pt-2">
                             <div>
                                 <span className="block text-gray-400 text-[9px]">Patient Portion</span>
                                 <span className="font-bold text-gray-700">$450.00</span>
                             </div>
                             <div>
                                 <span className="block text-gray-400 text-[9px]">History</span>
                                 <span className="font-bold text-gray-700">Last: 10/2019</span>
                             </div>
                         </div>
                     </div>
                     
                     {/* Breakdown Item 2 */}
                     <div className="glass-card bg-white/80 p-3 rounded-xl shadow-md border-l-4 border-l-blue-500 opacity-90 transform translate-x-4">
                         <div className="flex justify-between items-center">
                             <div className="flex items-center gap-2">
                                <div className="bg-blue-100 p-1 rounded text-blue-700 font-mono text-[10px] font-bold">D0120</div>
                                <span className="text-xs font-bold text-gray-800">Periodic Oral Eval</span>
                             </div>
                             <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">100% Covered</span>
                         </div>
                     </div>
                 </div>
              </div>
            </div>

            {/* Card 2 - Escalation Reports (Span 1) */}
            <div className="md:col-span-1 bg-white rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mb-6 text-red-600">
                  <Activity className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Escalation Reports</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Start your day knowing exactly which patients have inactive insurance or missing info.
                </p>
                
                {/* Visual */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 shadow-inner">
                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                        <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Morning Huddle</span>
                        <span className="text-[10px] text-gray-400">Dec 8</span>
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center gap-3 bg-white p-2 rounded-lg border border-red-100 shadow-sm">
                            <div className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0 animate-pulse"></div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-bold text-gray-900 truncate">John Smith</div>
                                <div className="text-[10px] text-red-600 truncate">Inactive Coverage</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 bg-white p-2 rounded-lg border border-yellow-100 shadow-sm">
                            <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full flex-shrink-0"></div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-bold text-gray-900 truncate">Sarah Jones</div>
                                <div className="text-[10px] text-yellow-600 truncate">Missing Group ID</div>
                            </div>
                        </div>
                    </div>
                </div>
              </div>
            </div>

            {/* Card 3 - Smart Form Filling (Span 1) */}
            <div className="md:col-span-1 bg-gray-50 rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm text-purple-600">
                  <ScanLine className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Smart Form Filling</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Upload your existing paper forms. Amanda uses AI to read them and fill them out.
                </p>
                
                {/* Visual */}
                <div className="relative bg-white rounded-xl p-4 border border-gray-200 shadow-sm h-32 overflow-hidden flex flex-col justify-center">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent via-purple-500/10 to-transparent scan-line"></div>
                    <div className="space-y-2 opacity-50">
                        <div className="h-2 bg-gray-200 rounded w-1/3"></div>
                        <div className="h-2 bg-purple-100 rounded w-full border border-purple-200"></div>
                        <div className="h-2 bg-gray-200 rounded w-1/4 mt-2"></div>
                        <div className="h-2 bg-purple-100 rounded w-3/4 border border-purple-200"></div>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-purple-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm flex items-center gap-1">
                        <Check className="w-3 h-3" /> Auto-Filled
                    </div>
                </div>
              </div>
            </div>

            {/* Card 4 - AI Insurance Calling (Span 1) */}
             <div className="md:col-span-1 bg-gray-50 rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm text-green-600">
                  <Phone className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">AI Insurance Calling</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Amanda calls payers, navigates IVR, and speaks to reps to verify benefits.
                </p>
                
                {/* Visual */}
                <div className="bg-white rounded-xl p-4 border border-green-100 shadow-sm relative overflow-hidden">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                            <Phone className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                            <div className="text-xs font-bold text-gray-900">Calling Payer...</div>
                            <div className="text-[10px] text-green-600 font-mono">00:42</div>
                        </div>
                    </div>
                    {/* Waveform */}
                    <div className="flex items-center justify-center gap-1 h-6">
                        {[1, 2, 3, 2, 4, 3, 5, 4, 2, 3].map((h, i) => (
                            <div
                                key={i}
                                className="w-1 bg-green-500 rounded-full animate-pulse"
                                style={{ height: `${h * 20}%`, animationDelay: `${i * 0.1}s` }}
                            ></div>
                        ))}
                    </div>
                </div>
              </div>
            </div>

            {/* Card 5 - AI Copilot (Span 1) */}
             <div className="md:col-span-1 bg-slate-900 rounded-[2rem] p-8 overflow-hidden relative group transition-all hover:shadow-xl text-white">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center mb-6 text-blue-400">
                  <Brain className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">AI Co-Pilot</h3>
                <p className="text-slate-400 leading-relaxed mb-6">
                  Ask questions about any patient's benefits in plain English.
                </p>
                
                {/* Visual */}
                <div className="space-y-3">
                    <div className="flex justify-end">
                        <div className="bg-slate-800 text-xs text-slate-300 py-2 px-3 rounded-t-xl rounded-bl-xl max-w-[85%] border border-slate-700">
                            Does Sarah have coverage for a nightguard?
                        </div>
                    </div>
                    <div className="flex justify-start">
                         <div className="bg-blue-600 text-xs text-white py-2 px-3 rounded-t-xl rounded-br-xl max-w-[90%] shadow-lg">
                            Yes, D9944 is covered at 50%.
                        </div>
                    </div>
                </div>
              </div>
               <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:16px_16px]"></div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: Advanced Capabilities Section */}
      <section className="py-24 px-6 bg-slate-900 text-white overflow-hidden relative">
        {/* Backgrounds */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />
        </div>

        <div className="container mx-auto max-w-7xl relative z-10">
            <div className="text-center mb-20">
                <h2 className="text-3xl md:text-5xl font-bold mb-6">
                    Beyond Basic Verification
                </h2>
                <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                    Amanda does more than just check eligibility. She's a complete insurance assistant.
                </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
                {/* AI Co-Pilot */}
                <div className="flex flex-col justify-center">
                    <div className="inline-flex items-center gap-2 text-blue-400 font-bold tracking-wider text-xs uppercase mb-4">
                        <Brain className="w-4 h-4" />
                        AI Co-Pilot
                    </div>
                    <h3 className="text-3xl font-bold mb-4">Ask any question in plain English.</h3>
                    <p className="text-slate-400 text-lg leading-relaxed mb-8">
                        Stop hunting through 10-page faxed breakdowns. Just ask Amanda: "Does this patient have coverage for a nightguard?" or "What's the frequency for bitewings?" and get an instant answer based on the verified benefits.
                    </p>
                    
                    <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700 shadow-xl">
                        <div className="space-y-4">
                            <div className="flex gap-4">
                                <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                                    <span className="text-xs font-bold">You</span>
                                </div>
                                <div className="bg-slate-700 rounded-2xl rounded-tl-none p-3 text-sm text-slate-200">
                                    Does Sarah have coverage for D2740 on tooth #14?
                                </div>
                            </div>
                            <div className="flex gap-4 flex-row-reverse">
                                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                                    <Sparkles className="w-4 h-4 text-white" />
                                </div>
                                <div className="bg-blue-600 rounded-2xl rounded-tr-none p-3 text-sm text-white">
                                    Yes, D2740 is covered at 50% Major.
                                    <br/>
                                    <br/>
                                    <span className="opacity-90 text-xs bg-blue-700 px-2 py-1 rounded inline-block">History Check:</span> Last done 4 years ago (eligible every 5 years).
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Smart Forms */}
                <div className="flex flex-col justify-center">
                    <div className="inline-flex items-center gap-2 text-purple-400 font-bold tracking-wider text-xs uppercase mb-4">
                        <ScanLine className="w-4 h-4" />
                        Smart Form Filling
                    </div>
                    <h3 className="text-3xl font-bold mb-4">Paper forms? No problem.</h3>
                    <p className="text-slate-400 text-lg leading-relaxed mb-8">
                        Still dealing with unique paper forms for some payers? Upload the PDF, and Amanda uses computer vision to read it and fill in all the verified patient and insurance details automatically.
                    </p>

                    <div className="relative bg-slate-800 rounded-2xl border border-slate-700 shadow-xl p-1 overflow-hidden h-[300px]">
                        <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(transparent,rgba(168,85,247,0.1),transparent)] h-1/2 animate-scan"></div>
                        <div className="bg-white/5 p-6 h-full rounded-xl flex flex-col gap-4 opacity-80">
                            {/* Mock Form */}
                            <div className="h-4 bg-slate-600 rounded w-1/3"></div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="h-8 bg-slate-700 rounded border border-purple-500/50 flex items-center px-3">
                                    <span className="text-xs text-purple-300">John Doe</span>
                                </div>
                                <div className="h-8 bg-slate-700 rounded border border-purple-500/50 flex items-center px-3">
                                    <span className="text-xs text-purple-300">12/05/1985</span>
                                </div>
                            </div>
                            <div className="h-8 bg-slate-700 rounded border border-purple-500/50 flex items-center px-3">
                                <span className="text-xs text-purple-300">Delta Dental of California</span>
                            </div>
                            <div className="h-24 bg-slate-700 rounded border border-purple-500/50 relative overflow-hidden">
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-xs text-purple-300 font-bold flex items-center gap-2">
                                        <CheckCircle className="w-3 h-3" /> Auto-Filled
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* Why AI Beats Humans */}
      <section className="py-14 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            
            {/* Left: Content */}
            <div>
              <p className="text-xs text-blue-600 font-bold uppercase tracking-wider mb-2.5">ROI & EFFICIENCY</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-3 leading-tight">
                Stop Wasting Hours on <br/>
                <span className="text-blue-600">Hold Music</span>
              </h2>

              <p className="text-base text-gray-600 mb-6 leading-relaxed">
                Your staff spends hours every day navigating IVR menus and waiting on hold. Amanda does this in the background, 24/7, so your team can focus on patients.
              </p>

              <div className="space-y-4">
                <div className="flex gap-3.5">
                  <div className="flex-shrink-0 w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1.5">1,225 Verifications Automate</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Rowland Family Dentistry automated over 1,000 eligibility checks, saving hundreds of hours of staff time on hold.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3.5">
                  <div className="flex-shrink-0 w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1.5">Reduce Denials & Surprise Bills</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Accurate, procedure-level breakdowns mean fewer surprises for patients and higher collection rates for you.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3.5">
                  <div className="flex-shrink-0 w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Activity className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1.5">Daily Morning Huddle Report</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Get a summary of all patients for the day, highlighting eligibility issues and opportunities.
                    </p>
                  </div>
                </div>
              </div>

            </div>

            {/* Right: Interactive Graphic */}
            <div className="relative h-[400px] bg-white rounded-3xl overflow-hidden border border-gray-200 shadow-xl flex items-center justify-center p-8">
               <div className="w-full max-w-sm">
                   <div className="flex items-center justify-between mb-8">
                       <div className="text-center">
                           <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-2 mx-auto grayscale">
                               <Users className="w-8 h-8 text-gray-400" />
                           </div>
                           <p className="font-bold text-gray-500">Manual</p>
                           <p className="text-xs text-gray-400">45 mins / patient</p>
                       </div>
                       <div className="flex-1 h-px bg-gray-200 mx-4 relative">
                           <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-100 px-2 text-xs text-gray-400">vs</div>
                       </div>
                       <div className="text-center">
                           <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-2 mx-auto shadow-md">
                               <Zap className="w-8 h-8 text-blue-600" />
                           </div>
                           <p className="font-bold text-blue-600">Amanda</p>
                           <p className="text-xs text-blue-400">Instant + Auto</p>
                       </div>
                   </div>

                   <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                       <h4 className="font-bold text-gray-900 mb-2 text-sm">Task: Verify Crown Coverage</h4>
                       
                       <div className="space-y-3">
                           <div className="relative pt-1">
                               <div className="flex mb-2 items-center justify-between">
                                   <div className="text-xs font-semibold text-blue-600 py-1 px-2 rounded bg-blue-100 uppercase">
                                     Amanda
                                   </div>
                                   <div className="text-right">
                                       <span className="text-xs font-semibold inline-block text-blue-600">
                                           Done (2 min)
                                       </span>
                                   </div>
                               </div>
                               <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-100">
                                   <div style={{ width: "100%" }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"></div>
                               </div>
                           </div>

                           <div className="relative pt-1 opacity-50">
                               <div className="flex mb-2 items-center justify-between">
                                   <div className="text-xs font-semibold text-gray-600 py-1 px-2 rounded bg-gray-200 uppercase">
                                     Staff
                                   </div>
                                   <div className="text-right">
                                       <span className="text-xs font-semibold inline-block text-gray-600">
                                           Hold... (25 min)
                                       </span>
                                   </div>
                               </div>
                               <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
                                   <div style={{ width: "40%" }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gray-500 animate-pulse"></div>
                               </div>
                           </div>
                       </div>
                   </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-blue-700 shadow-xl border border-blue-500/50">
            <div className="grid lg:grid-cols-2 gap-0 items-center">
              {/* Left: Content */}
              <div className="p-10 lg:p-12 relative z-10">
                <p className="text-xs font-bold text-blue-100 tracking-wider mb-3 uppercase">
                  GET STARTED TODAY
                </p>
                
                <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white leading-tight">
                  Ready to eliminate insurance write-offs?
                </h2>
                
                <p className="text-base text-blue-100 mb-6 leading-relaxed max-w-xl">
                  Join the forward-thinking practices that have automated their insurance verification process. Save time, reduce errors, and increase collections.
                </p>
                
                <button className="group px-7 py-3 rounded-xl font-bold text-sm transition-all bg-white text-blue-600 hover:bg-blue-50 shadow-lg hover:shadow-xl">
                  <span className="flex items-center gap-2">
                    Book a Demo
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
              </div>

              {/* Right: Graphic Composition */}
              <div className="relative h-full min-h-[400px] flex items-center justify-center p-8 overflow-hidden">
                {/* Background decorative elements */}
                <div className="absolute inset-0">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
                </div>

                {/* Main Card */}
                <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-6 w-72 transform rotate-2 hover:rotate-0 transition-transform duration-500">
                  <div className="flex flex-col items-center mb-6">
                    <div className="relative mb-3">
                      <div className="w-20 h-20 rounded-full p-1 bg-gradient-to-br from-blue-100 to-indigo-100">
                        <img
                          src="/assets/amanda.png"
                          alt="Amanda"
                          className="w-full h-full rounded-full object-cover"
                        />
                      </div>
                      <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-sm">
                        <div className="bg-blue-600 rounded-full p-1">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Amanda</h3>
                    <p className="text-sm text-blue-600 font-medium">Insurance Agent</p>
                  </div>

                  <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                          <span className="text-gray-600">Eligibility</span>
                          <span className="text-green-600 font-bold">Verified</span>
                      </div>
                      <div className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                          <span className="text-gray-600">Breakdown</span>
                          <span className="text-green-600 font-bold">Synced</span>
                      </div>
                      <div className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                          <span className="text-gray-600">Write-offs</span>
                          <span className="text-green-600 font-bold">Avoided</span>
                      </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InsuranceVerificationPage;
